import FetchClient, { type ApiResponse } from "./FetchClient";

/**
 * Onboarding store preview (GET /onboarding/store-preview): real deals shown
 * between the ZIP and the plan screen. Prices are exact; aisles and stock stay
 * behind the subscription. Keep in sync with pinto/services/OnboardingPreviewService.ts.
 */
export type OnboardingPreviewMode = "own" | "nearby" | "leads" | "none";

export interface OnboardingPreviewStore {
  storeId: string;
  address: string;
  distanceMiles: number;
  dealCount: number;
}

export interface OnboardingPreviewDeal {
  name: string;
  image: string;
  price: number;
  wasPrice: number;
  discountPercent: number;
  storeId: string;
  distanceMiles: number;
  /** Only on the first few deals, which onboarding shows in full. */
  aisle?: string;
  quantity?: number;
}

export interface OnboardingPreviewLead {
  title: string;
  image: string;
  price: number;
  wasPrice: number;
  discountPercent: number;
  category: string;
}

export interface OnboardingPreview {
  mode: OnboardingPreviewMode;
  zipCode: string;
  retailer: string;
  stores: OnboardingPreviewStore[];
  dealCount: number;
  deals: OnboardingPreviewDeal[];
  leads: OnboardingPreviewLead[];
  leadCountWeek: number;
  leadCountDay: number;
  /** Markdown dollars across every counted deal, and how many single finds save at least `paybackDollars`. */
  savingsTotal: number;
  paybackDeals: number;
  paybackDollars: number;
}

const MODES = new Set<OnboardingPreviewMode>(["own", "nearby", "leads", "none"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);
const num = (value: unknown): number => (typeof value === "number" && Number.isFinite(value) ? value : 0);
const str = (value: unknown): string => (typeof value === "string" ? value : "");

/** Anything malformed becomes mode "none", so the paywall simply skips the preview. */
export function parseOnboardingPreview(value: unknown): OnboardingPreview {
  const empty: OnboardingPreview = {
    mode: "none", zipCode: "", retailer: "walmart", stores: [], dealCount: 0, deals: [], leads: [], leadCountWeek: 0, leadCountDay: 0,
    savingsTotal: 0, paybackDeals: 0, paybackDollars: 0,
  };
  if (!isRecord(value) || !MODES.has(value.mode as OnboardingPreviewMode)) return empty;
  const stores = Array.isArray(value.stores) ? value.stores.filter(isRecord).map((s) => ({
    storeId: str(s.storeId), address: str(s.address), distanceMiles: num(s.distanceMiles), dealCount: num(s.dealCount),
  })).filter((s) => s.storeId) : [];
  const deals = Array.isArray(value.deals) ? value.deals.filter(isRecord).map((d) => ({
    name: str(d.name), image: str(d.image), price: num(d.price), wasPrice: num(d.wasPrice),
    discountPercent: num(d.discountPercent), storeId: str(d.storeId), distanceMiles: num(d.distanceMiles),
    aisle: str(d.aisle) || undefined, quantity: num(d.quantity) || undefined,
  })).filter((d) => d.name && d.price > 0 && d.wasPrice > d.price) : [];
  const leads = Array.isArray(value.leads) ? value.leads.filter(isRecord).map((l) => ({
    title: str(l.title), image: str(l.image), price: num(l.price), wasPrice: num(l.wasPrice),
    discountPercent: num(l.discountPercent), category: str(l.category),
  })).filter((l) => l.title && l.price > 0 && l.wasPrice > l.price) : [];
  return {
    mode: value.mode as OnboardingPreviewMode,
    zipCode: str(value.zipCode),
    retailer: str(value.retailer) || "walmart",
    stores,
    dealCount: num(value.dealCount),
    deals,
    leads,
    leadCountWeek: num(value.leadCountWeek),
    leadCountDay: num(value.leadCountDay),
    savingsTotal: num(value.savingsTotal),
    paybackDeals: num(value.paybackDeals),
    paybackDollars: num(value.paybackDollars),
  };
}

export async function getOnboardingStorePreview(zipCode: string): Promise<OnboardingPreview> {
  const response = await FetchClient.get<ApiResponse<unknown>>(`/onboarding/store-preview?zipCode=${encodeURIComponent(zipCode)}`);
  return parseOnboardingPreview(response.data);
}
